import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    SimpleChanges,
} from "@angular/core";
import { TOOLTIP_HIDE_DELAY_MS_DEFAULT, TooltipKind, TooltipShowTrigger } from "@spscommerce/ds-shared";
import { Position, PositioningService } from "@spscommerce/positioning";
import autobind from "autobind-decorator";

@Component({
    selector: "sps-tooltip",
    templateUrl: "./sps-tooltip.template.html",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpsTooltipComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    static readonly displayName = "sps-tooltip";
    static readonly props = {
        for: { type: "string | HTMLElement | ElementRef", required: true },
        title: "string",
        position: "Position",
        offsets: "string",
        showOn: "TooltipShowTrigger",
        kind: "TooltipKind",
        hideDelay: "number",
    };

    private static _instances = new Set<SpsTooltipComponent>();
    private static _openTips = new Set<SpsTooltipComponent>();

    @Input() for: string | HTMLElement | ElementRef;
    @Input() title: string;
    @Input() position: Position = Position.TOP_MIDDLE;
    @Input() offsets = "13, 0";
    @Input() showOn: TooltipShowTrigger = TooltipShowTrigger.MOUSEOVER;
    @Input() kind: TooltipKind = TooltipKind.DEFAULT;
    @Input() hideDelay: number = TOOLTIP_HIDE_DELAY_MS_DEFAULT;

    @HostBinding("class.sps-tooltip")
    @HostBinding("class.z-stratum-tip")
    readonly _staticClassBindings = true;

    @HostBinding("class.sps-tooltip--shown")
    _isShown = false;

    @HostBinding("attr.role") readonly _role = "tooltip";

    private _parsedOffsets = [13, 0];
    private _hideTimer: number;
    private _positioningService = new PositioningService();
    private _attachedTo: ElementRef;

    get isShown() {
        return this._isShown;
    }
    get attachedTo() {
        return this._attachedTo;
    }

    get positioningOptions() {
        return {
            relativeTo: this.attachedTo.nativeElement,
            position: this.position,
            offsets: this._parsedOffsets,
        };
    }

    constructor(
        private changeDetector: ChangeDetectorRef,
        private elementRef: ElementRef,
        private renderer: Renderer2,
    ) {
        if (SpsTooltipComponent._instances.size === 0) {
            document.addEventListener("mouseover", SpsTooltipComponent._onDocumentMouseover);
        }

        SpsTooltipComponent._instances.add(this);
    }

    static _onDocumentMouseover(event: MouseEvent) {
        for (const instance of SpsTooltipComponent._instances) {
            if (instance.showOn === TooltipShowTrigger.MOUSEOVER) {
                if (instance._attachedTo.nativeElement.contains(event.target)) {
                    instance.show();
                } else {
                    instance.delayedHide();
                }
            }
        }
    }

    ngOnInit() {
        this.addClass(`sps-tooltip--${this.kind}`);
        this.addClass(`sps-tooltip--pos-${this.position.replace(" ", "-")}`);
    }

    ngAfterViewInit() {
        this.updateAttachment();

        if (this.showOn === TooltipShowTrigger.CLICK) {
            this._attachedTo.nativeElement.addEventListener("click", this.toggle);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.for) {
            this.updateAttachment();
        }

        if (changes.kind) {
            this.replaceClass(
                `sps-tooltip--${changes.kind.previousValue}`,
                `sps-tooltip--${changes.kind.currentValue}`
            );
        }

        if (changes.position && !changes.position.firstChange) {
            this.replaceClass(
                `sps-tooltip--pos-${changes.position.previousValue.replace(" ", "-")}`,
                `sps-tooltip--pos-${changes.position.currentValue.replace(" ", "-")}`
            );
        }

        if (changes.offsets) {
            this._parsedOffsets = changes.offsets.currentValue
                .split(/, ?/)
                .map(offset => parseInt(offset, 10));
        }

        if (this._isShown) {
            this.refresh();
        }
    }

    ngOnDestroy() {
        if (SpsTooltipComponent._instances.has(this)) {
            SpsTooltipComponent._instances.delete(this);

            if (SpsTooltipComponent._instances.size === 0) {
                document.removeEventListener("mouseover", SpsTooltipComponent._onDocumentMouseover);
            }
        }
    }

    refresh() {
        this._positioningService.reposition(this.elementRef.nativeElement, this.positioningOptions);
    }

    private updateAttachment() {
        if (this.for instanceof ElementRef) {
            this._attachedTo = this.for;
        } else {
            const target = this.for instanceof HTMLElement
                ? this.for
                : document.getElementById(this.for) ||
                this.elementRef.nativeElement.parentElement.querySelector(`[name="${this.for}"]`) ||
                document.querySelector(`[name="${this.for}"]`);
            this._attachedTo = new ElementRef(target);
        }
    }

    @autobind
    show(defer = false): boolean {
        // If defer is true, the tooltip will only open if no other tooltip is already active.
        if (defer && SpsTooltipComponent._openTips.size > 0) {
            return false;
        }

        if (this.elementRef.nativeElement.textContent === "") {
            console.warn("sps-tooltip is empty; aborting show");
            return false;
        }

        if (!this._isShown) {
            for (const tip of SpsTooltipComponent._openTips) {
                tip.hide();
            }
            SpsTooltipComponent._openTips.add(this);
            this._clearHideDelay();

            this._isShown = true;
            this.changeDetector.markForCheck();
            this._positioningService.position(this.elementRef.nativeElement, this.positioningOptions);
        }
        return true;
    }

    @autobind
    hide() {
        if (this._isShown) {
            if (SpsTooltipComponent._openTips.has(this)) {
                SpsTooltipComponent._openTips.delete(this);
            }
            this._clearHideDelay();

            this._isShown = false;
            this.changeDetector.markForCheck();
            this._positioningService.release(this.elementRef.nativeElement);
        }
    }

    @autobind
    delayedHide() {
        this._clearHideDelay();
        this._hideTimer = window.setTimeout(this.hide, this.hideDelay);
    }

    @autobind
    toggle() {
        if (this._isShown) {
            this.hide();
        } else {
            this.show();
        }
    }

    @autobind
    @HostListener("mouseout")
    _onMouseout() {
        if (this.showOn === TooltipShowTrigger.MOUSEOVER) {
            this.delayedHide();
        }
    }

    @HostListener("mouseover")
    _clearHideDelay() {
        if (this._hideTimer) {
            clearTimeout(this._hideTimer);
            delete this._hideTimer;
        }
    }

    addClass(className: string) {
        this.renderer.addClass(this.elementRef.nativeElement, className);
    }

    replaceClass(oldClassName: string, newClassName: string) {
        this.renderer.removeClass(this.elementRef.nativeElement, oldClassName);
        this.addClass(newClassName);
    }
}
